import React, { useState, useEffect, useRef } from 'react';
import { Heart, Apple, Gamepad2, Sparkles, Plus, ShoppingBag, Star, Upload, Trash2, ImageIcon, Loader2 } from 'lucide-react';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { useAuth } from '../context/AuthContext';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../components/ui/dialog';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../components/ui/tabs';
import api from '../lib/api';
import { toast } from 'sonner';

const RARITY_RING = {
  common:    'border-white/10',
  rare:      'border-cyan-400/40',
  epic:      'border-fuchsia-400/50',
  legendary: 'border-amber-400/60',
  mythic:    'border-rose-400/70',
};

const RARITY_OPTIONS = [
  { id: 'common',    label: 'Common',    color: 'text-white/70',     gradient: 'from-cyan-500 to-blue-600' },
  { id: 'rare',      label: 'Rare',      color: 'text-cyan-300',     gradient: 'from-pink-500 to-purple-600' },
  { id: 'epic',      label: 'Epic',      color: 'text-fuchsia-300',  gradient: 'from-fuchsia-500 to-violet-600' },
  { id: 'legendary', label: 'Legendary', color: 'text-amber-300',    gradient: 'from-amber-400 to-orange-500' },
  { id: 'mythic',    label: 'Mythic',    color: 'text-rose-300',     gradient: 'from-rose-500 to-amber-500' },
];

// Resize/compress a file to a ≤ 512×512 JPEG data URL so we don't bloat MongoDB.
async function compressImage(file, maxSize = 512, quality = 0.85) {
  const img = await new Promise((resolve, reject) => {
    const i = new Image();
    i.onload = () => resolve(i);
    i.onerror = reject;
    i.src = URL.createObjectURL(file);
  });
  const scale = Math.min(1, maxSize / Math.max(img.width, img.height));
  const w = Math.round(img.width * scale);
  const h = Math.round(img.height * scale);
  const canvas = document.createElement('canvas');
  canvas.width = w; canvas.height = h;
  canvas.getContext('2d').drawImage(img, 0, 0, w, h);
  URL.revokeObjectURL(img.src);
  return canvas.toDataURL('image/jpeg', quality);
}

export default function Pets() {
  const { user, refresh, creditStars, openBuyStars } = useAuth();
  const [pet, setPet] = useState(() => JSON.parse(localStorage.getItem('wv_pet') || 'null'));
  const [picker, setPicker] = useState(false);
  const [createOpen, setCreateOpen] = useState(false);
  const [customPets, setCustomPets] = useState([]);
  const [petsLoading, setPetsLoading] = useState(true);
  const [tick, setTick] = useState(0);
  const [items, setItems] = useState([]);
  const [inv, setInv] = useState({});
  const [buying, setBuying] = useState(null);
  const [bounce, setBounce] = useState(0); // increment to retrigger CSS animation

  // Load shop catalog, inventory, and the user's custom pets
  useEffect(() => {
    api.get('/pets/items').then(r => setItems(r.data || [])).catch(() => {});
    api.get('/pets/my-items').then(r => {
      const map = {};
      (r.data || []).forEach(d => { map[d.item_id] = d.qty; });
      setInv(map);
    }).catch(() => {});
    reloadCustomPets();
  }, []);

  const reloadCustomPets = async () => {
    setPetsLoading(true);
    try {
      const r = await api.get('/pets/custom');
      setCustomPets(r.data || []);
    } catch {
      // silent — UI will show empty state
    } finally {
      setPetsLoading(false);
    }
  };

  // Gentle decay over time
  useEffect(() => {
    if (!pet) return;
    const t = setInterval(() => setTick(x => x + 1), 30000);
    return () => clearInterval(t);
  }, [pet]);

  useEffect(() => {
    if (!pet) return;
    setPet(p => p ? ({ ...p, hunger: Math.max(0, (p.hunger ?? 80) - 0.5), happiness: Math.max(0, (p.happiness ?? 80) - 0.3) }) : p);
  }, [tick]);

  const persist = (p) => { localStorage.setItem('wv_pet', JSON.stringify(p)); setPet(p); };
  const happyBounce = () => setBounce(b => b + 1);

  const adopt = async (sp) => {
    // Optimistic UI first so the toast+care panel feel instant.
    persist({
      id: sp.id,
      name: sp.name,
      tag: sp.tag,
      rarity: sp.rarity,
      color: sp.color,
      emoji: sp.emoji,
      img: sp.image_data_url || null,
      level: 1, xp: 0, hunger: 80, happiness: 80, adoptedAt: Date.now(),
    });
    setPicker(false);
    toast.success(`You adopted ${sp.name} ${sp.emoji || ''}`);
    // Persist to backend so the adoption is visible on the user's public
    // profile ("Pets" section) and syncs across devices. Silent failure is
    // fine — the localStorage copy still drives the current-device UI.
    try {
      await api.post('/pets/adopt', { pet_id: sp.id });
    } catch (err) {
      console.warn('[pets] adopt persist failed', err);
    }
  };

  const deleteCustom = async (sp) => {
    if (sp.featured) {
      toast.info('WorldVibe-curated pets can\'t be removed.');
      return;
    }
    if (!confirm(`Delete "${sp.name}" from your collection? This can't be undone.`)) return;
    try {
      await api.delete(`/pets/custom/${sp.id}`);
      setCustomPets(prev => prev.filter(p => p.id !== sp.id));
      // If user deleted their currently-adopted pet, clear it
      if (pet?.id === sp.id) { localStorage.removeItem('wv_pet'); setPet(null); }
      toast.success(`Removed ${sp.name}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not delete');
    }
  };

  const feed = () => {
    if (!pet) return;
    const next = { ...pet, hunger: Math.min(100, pet.hunger + 25), xp: pet.xp + 8 };
    if (next.xp >= 100) { next.xp = next.xp - 100; next.level = (next.level || 1) + 1; toast.success(`${pet.name} reached Level ${next.level}!`); }
    persist(next);
    happyBounce();
    toast(`${pet.name} ate happily 🍎`);
  };

  const play = () => {
    if (!pet) return;
    const next = { ...pet, happiness: Math.min(100, pet.happiness + 30), xp: pet.xp + 12, hunger: Math.max(0, pet.hunger - 5) };
    if (next.xp >= 100) { next.xp = next.xp - 100; next.level = (next.level || 1) + 1; toast.success(`${pet.name} reached Level ${next.level}!`); }
    persist(next);
    happyBounce();
    toast(`${pet.name} had fun playing!`);
  };

  const rest = () => {
    if (!pet) return;
    persist({ ...pet, happiness: Math.min(100, pet.happiness + 10), xp: pet.xp + 4 });
    happyBounce();
    toast(`${pet.name} took a nap ✨`);
  };

  const buyItem = async (it) => {
    if ((user?.stars ?? 0) < it.price) {
      toast.error(`Need ${(it.price - (user?.stars ?? 0)).toLocaleString()} more ⭐ for ${it.name}.`);
      openBuyStars();
      return;
    }
    setBuying(it.id);
    try {
      const { data } = await api.post(`/pets/items/${it.id}/buy`);
      creditStars(-it.price);
      setInv(prev => ({ ...prev, [it.id]: (prev[it.id] || 0) + 1 }));
      toast.success(`Bought ${it.name} ${it.emoji}`);
      refresh?.();
      if (pet) {
        const ef = it.effect || {};
        const next = {
          ...pet,
          hunger:    Math.min(100, (pet.hunger ?? 80) + (ef.hunger || 0)),
          happiness: Math.min(100, (pet.happiness ?? 80) + (ef.happiness || 0)),
          xp:        (pet.xp || 0) + (ef.xp || 0),
        };
        if (next.xp >= 100) { next.xp -= 100; next.level = (next.level || 1) + 1; toast.success(`${pet.name} reached Level ${next.level}!`); }
        persist(next);
      }
    } catch (e) {
      const detail = e.response?.data?.detail || '';
      if (/not enough stars/i.test(detail)) { openBuyStars(); toast.error('Not enough stars — open Buy Stars to top up'); }
      else toast.error(detail || 'Could not buy item');
    } finally {
      setBuying(null);
    }
  };

  return (
    <div className="space-y-5">
      <div className="flex items-start justify-between flex-wrap gap-3">
        <div>
          <h1 className="font-display font-bold text-2xl md:text-3xl">Cosmic Pets</h1>
          <p className="text-white/60 text-sm mt-1">Create, adopt, feed, play — your companion across the universe.</p>
        </div>
        {pet && (
          <Button
            onClick={() => setPicker(true)}
            variant="outline"
            className="border-white/15 bg-white/5 hover:bg-white/10 text-white"
            data-testid="pets-adopt-another"
          >
            <Plus size={14} className="mr-1" /> Adopt another
          </Button>
        )}
      </div>

      {pet ? (
        <Card className="bg-[#161622] border-white/10 overflow-hidden">
          <div className={`mx-auto mt-6 w-full max-w-[280px] aspect-square relative bg-gradient-to-br ${pet.color || 'from-cyan-500 to-blue-600'} overflow-hidden rounded-2xl`}>
            {pet.img ? (
              <img
                key={bounce}
                src={pet.img}
                alt={pet.name}
                className="absolute inset-0 w-full h-full object-cover pet-bounce"
              />
            ) : (
              <>
                <div className="absolute inset-0 bg-black/20" />
                <div key={bounce} className="absolute inset-0 flex items-center justify-center text-8xl drop-shadow-[0_0_30px_rgba(255,255,255,0.4)] pet-bounce">{pet.emoji}</div>
              </>
            )}
            <div className="absolute top-2.5 left-2.5 text-[10px] uppercase tracking-wider bg-black/55 backdrop-blur px-2 py-0.5 rounded-full font-semibold">{pet.tag}</div>
          </div>
          <CardContent className="p-5">
            <div className="flex items-center justify-between">
              <div>
                <h2 className="font-display font-bold text-xl">{pet.name}</h2>
                <div className="text-xs text-white/50">Level {pet.level}</div>
              </div>
              <div className="text-right">
                <div className="text-xs text-white/50">XP</div>
                <div className="text-amber-300 font-semibold">{pet.xp}/100</div>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4 mt-4">
              <Meter label="Hunger" value={pet.hunger} icon={<Apple size={12} className="text-emerald-300" />} color="bg-emerald-400" />
              <Meter label="Happiness" value={pet.happiness} icon={<Heart size={12} className="text-pink-300" />} color="bg-pink-400" />
            </div>

            <div className="mt-5 grid grid-cols-3 gap-3">
              <Button onClick={feed} className="bg-emerald-500 hover:bg-emerald-600 text-white border-0"><Apple size={14} className="mr-2" />Feed</Button>
              <Button onClick={play} className="bg-pink-500 hover:bg-pink-600 text-white border-0"><Gamepad2 size={14} className="mr-2" />Play</Button>
              <Button onClick={rest} variant="outline" className="border-white/15 bg-white/5 hover:bg-white/10 text-white"><Sparkles size={14} className="mr-2" />Rest</Button>
            </div>
          </CardContent>
        </Card>
      ) : (
        <Card className="bg-[#161622] border-white/10">
          <CardContent className="p-10 text-center">
            <div className="text-6xl mb-3">🪐</div>
            <div className="font-display font-semibold text-lg">Create your first pet</div>
            <div className="text-sm text-white/60 mt-1">Design a one-of-a-kind cosmic companion — upload a picture, give them a name, and pick their rarity.</div>
            <Button
              onClick={() => setPicker(true)}
              data-testid="pets-choose-pet"
              className="mt-5 bg-gradient-to-r from-fuchsia-500 to-cyan-500 text-white border-0 rounded-full"
            >
              <Plus size={14} className="mr-1.5" /> Create a pet
            </Button>
          </CardContent>
        </Card>
      )}

      {/* === Pet Shop === */}
      <Card className="bg-[#161622] border-white/10" data-testid="pet-shop">
        <CardContent className="p-5">
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-2">
              <ShoppingBag size={18} className="text-amber-300" />
              <div>
                <h2 className="font-display font-bold text-lg">Pet Shop</h2>
                <div className="text-xs text-white/50">Spend stars to keep your companion thriving</div>
              </div>
            </div>
          </div>

          <Tabs defaultValue="all">
            <TabsList className="bg-white/5">
              <TabsTrigger value="all">All</TabsTrigger>
              <TabsTrigger value="food">Food</TabsTrigger>
              <TabsTrigger value="toy">Toys</TabsTrigger>
              <TabsTrigger value="accessory">Accessories</TabsTrigger>
              <TabsTrigger value="boost">Boosts</TabsTrigger>
            </TabsList>

            {['all', 'food', 'toy', 'accessory', 'boost'].map(cat => (
              <TabsContent key={cat} value={cat} className="mt-3">
                <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3">
                  {items.filter(i => cat === 'all' || i.kind === cat).map(it => {
                    const owned = inv[it.id] || 0;
                    return (
                      <div
                        key={it.id}
                        data-testid={`pet-item-${it.id}`}
                        className={`rounded-2xl p-3 bg-white/3 border ${RARITY_RING[it.rarity] || 'border-white/10'} text-center hover:bg-white/5 transition`}
                      >
                        <div className="text-4xl mb-1">{it.emoji}</div>
                        <div className="font-semibold text-sm truncate">{it.name}</div>
                        <div className="text-[10px] uppercase tracking-wider text-white/40 mb-2">{it.rarity}</div>
                        <Button
                          onClick={() => buyItem(it)}
                          disabled={buying === it.id}
                          data-testid={`pet-item-buy-${it.id}`}
                          className="w-full h-8 bg-gradient-to-r from-amber-500 to-orange-500 hover:from-amber-400 hover:to-orange-400 text-white text-xs border-0"
                        >
                          <Star size={11} className="mr-1" fill="currentColor" />{it.price}
                        </Button>
                        {owned > 0 && <div className="text-[10px] text-emerald-300 mt-1.5">Owned ×{owned}</div>}
                      </div>
                    );
                  })}
                </div>
                {items.filter(i => cat === 'all' || i.kind === cat).length === 0 && (
                  <div className="text-center text-white/40 text-sm py-6">No items in this category yet.</div>
                )}
              </TabsContent>
            ))}
          </Tabs>
        </CardContent>
      </Card>

      {/* === Adopt picker === shows the user's own custom pets */}
      <Dialog open={picker} onOpenChange={setPicker}>
        <DialogContent className="bg-[#161622] border-white/10 text-white max-w-2xl" data-testid="pets-picker">
          <DialogHeader>
            <DialogTitle className="font-display">Your pet collection</DialogTitle>
            <DialogDescription className="text-white/55">
              Pick one to adopt, or create a new cosmic companion.
            </DialogDescription>
          </DialogHeader>

          <div className="flex justify-end -mt-2">
            <Button
              onClick={() => { setPicker(false); setCreateOpen(true); }}
              data-testid="pets-create-btn"
              className="bg-gradient-to-r from-fuchsia-500 to-cyan-500 text-white border-0 rounded-full"
            >
              <Plus size={14} className="mr-1.5" /> Create new pet
            </Button>
          </div>

          {petsLoading ? (
            <div className="py-10 text-center text-white/45 text-sm flex items-center justify-center gap-2">
              <Loader2 size={14} className="animate-spin" /> Loading your pets…
            </div>
          ) : customPets.length === 0 ? (
            <div className="py-10 text-center" data-testid="pets-empty-state">
              <div className="text-5xl mb-3">🪐</div>
              <div className="font-semibold text-white/80">No pets yet</div>
              <div className="text-xs text-white/50 mt-1">Create your first companion to begin the journey.</div>
            </div>
          ) : (
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 mt-2 max-h-[60vh] overflow-y-auto scroll-hide">
              {customPets.map(s => (
                <div key={s.id} className={`relative group rounded-2xl overflow-hidden bg-gradient-to-br ${s.color || 'from-cyan-500 to-blue-600'} text-center`}>
                  {s.featured && (
                    <div className="absolute top-2 left-2 z-10 text-[9px] uppercase tracking-wider bg-black/55 backdrop-blur px-1.5 py-0.5 rounded-full font-bold text-amber-200 flex items-center gap-1">
                      <Sparkles size={9} /> Featured
                    </div>
                  )}
                  {!s.featured && (
                    <button
                      type="button"
                      onClick={() => deleteCustom(s)}
                      data-testid={`pets-delete-${s.id}`}
                      className="absolute top-2 right-2 z-10 w-7 h-7 rounded-full bg-black/50 backdrop-blur opacity-0 group-hover:opacity-100 focus:opacity-100 transition flex items-center justify-center text-rose-300 hover:text-rose-200 hover:bg-rose-500/30"
                      aria-label={`Delete ${s.name}`}
                      title={`Delete ${s.name}`}
                    >
                      <Trash2 size={13} />
                    </button>
                  )}
                  <button
                    type="button"
                    onClick={() => adopt(s)}
                    data-testid={`pets-adopt-${s.id}`}
                    className="w-full text-left hover:-translate-y-0.5 transition"
                  >
                    <div className="aspect-square relative overflow-hidden group-hover:scale-105 transition-transform">
                      {s.image_data_url ? (
                        <img src={s.image_data_url} alt={s.name} className="absolute inset-0 w-full h-full object-cover" />
                      ) : (
                        <div className="absolute inset-0 flex items-center justify-center"><span className="text-6xl">{s.emoji}</span></div>
                      )}
                    </div>
                    <div className="bg-black/55 backdrop-blur p-3">
                      <div className="font-semibold text-sm truncate">{s.name}</div>
                      <div className="text-[10px] uppercase text-white/70">{s.tag}</div>
                    </div>
                  </button>
                </div>
              ))}
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* === Create pet dialog === */}
      <CreatePetDialog
        open={createOpen}
        onOpenChange={setCreateOpen}
        onCreated={async (pet) => {
          await reloadCustomPets();
          setCreateOpen(false);
          // Immediately open picker so the user can adopt their fresh pet
          setPicker(true);
        }}
      />
    </div>
  );
}

function CreatePetDialog({ open, onOpenChange, onCreated }) {
  const [name, setName] = useState('');
  const [emoji, setEmoji] = useState('');
  const [rarity, setRarity] = useState('common');
  const [imgUrl, setImgUrl] = useState(null);
  const [busy, setBusy] = useState(false);
  const fileRef = useRef(null);

  useEffect(() => {
    if (!open) {
      setName(''); setEmoji(''); setRarity('common'); setImgUrl(null); setBusy(false);
    }
  }, [open]);

  const onPickFile = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (!/^image\//.test(f.type)) { toast.error('Please choose an image file.'); return; }
    if (f.size > 8 * 1024 * 1024) { toast.error('Image is too large (max 8 MB before compression).'); return; }
    try {
      const dataUrl = await compressImage(f);
      setImgUrl(dataUrl);
    } catch {
      toast.error('Could not read this image.');
    }
  };

  const canSubmit = name.trim().length >= 2 && (imgUrl || emoji.trim()) && !busy;

  const submit = async () => {
    if (!canSubmit) return;
    setBusy(true);
    try {
      const { data } = await api.post('/pets/custom', {
        name: name.trim(),
        rarity,
        emoji: emoji.trim() || null,
        image_data_url: imgUrl || null,
      });
      toast.success(`Created ${data.pet.name}`);
      onCreated?.(data.pet);
    } catch (e) {
      const d = e?.response?.data?.detail;
      toast.error(typeof d === 'string' ? d : 'Could not create pet');
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-[#161622] border-white/10 text-white max-w-md" data-testid="pets-create-dialog">
        <DialogHeader>
          <DialogTitle className="font-display flex items-center gap-2">
            <Plus size={16} className="text-fuchsia-400" /> Create a pet
          </DialogTitle>
          <DialogDescription className="text-white/55">
            Upload a picture (or use an emoji), name them, and pick how rare they are.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          {/* Image picker */}
          <div>
            <Label className="text-[11px] uppercase tracking-wider text-white/50">Picture</Label>
            <div className="mt-1.5 flex items-center gap-3">
              <button
                type="button"
                onClick={() => fileRef.current?.click()}
                data-testid="pets-image-picker"
                className="w-24 h-24 rounded-2xl border-2 border-dashed border-white/15 bg-white/3 hover:bg-white/8 hover:border-white/30 transition flex items-center justify-center text-white/45 overflow-hidden"
              >
                {imgUrl ? (
                  <img src={imgUrl} alt="" className="w-full h-full object-cover" />
                ) : (
                  <ImageIcon size={26} />
                )}
              </button>
              <div className="flex-1 text-xs text-white/55 leading-relaxed">
                <div>PNG, JPG, WEBP, or GIF</div>
                <div className="text-white/35">Auto-resized to 512×512. ≤ 500 KB.</div>
                {imgUrl && (
                  <button
                    type="button"
                    onClick={() => { setImgUrl(null); if (fileRef.current) fileRef.current.value = ''; }}
                    className="mt-1 text-rose-300 hover:text-rose-200 text-[11px]"
                  >
                    Remove
                  </button>
                )}
              </div>
              <input
                ref={fileRef}
                type="file"
                accept="image/png,image/jpeg,image/webp,image/gif"
                onChange={onPickFile}
                hidden
                data-testid="pets-image-input"
              />
            </div>
          </div>

          {/* Name + emoji */}
          <div>
            <Label className="text-[11px] uppercase tracking-wider text-white/50">Name</Label>
            <Input
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="e.g. Astro the Pup"
              maxLength={40}
              data-testid="pets-name-input"
              className="mt-1.5 bg-white/5 border-white/10 text-white placeholder:text-white/40"
            />
          </div>

          <div>
            <Label className="text-[11px] uppercase tracking-wider text-white/50">
              Emoji fallback <span className="lowercase text-white/30">(optional, used if no image)</span>
            </Label>
            <Input
              value={emoji}
              onChange={(e) => setEmoji(e.target.value)}
              placeholder="🐉"
              maxLength={8}
              data-testid="pets-emoji-input"
              className="mt-1.5 bg-white/5 border-white/10 text-white placeholder:text-white/40"
            />
          </div>

          {/* Rarity */}
          <div>
            <Label className="text-[11px] uppercase tracking-wider text-white/50">Rarity</Label>
            <div className="mt-1.5 grid grid-cols-5 gap-1.5">
              {RARITY_OPTIONS.map(r => {
                const active = rarity === r.id;
                return (
                  <button
                    key={r.id}
                    type="button"
                    onClick={() => setRarity(r.id)}
                    data-testid={`pets-rarity-${r.id}`}
                    className={`px-2 py-2 rounded-lg text-[11px] font-semibold border transition ${
                      active
                        ? `bg-gradient-to-br ${r.gradient} border-white/30 text-white`
                        : 'bg-white/3 border-white/10 text-white/60 hover:bg-white/8'
                    }`}
                  >
                    {r.label}
                  </button>
                );
              })}
            </div>
          </div>
        </div>

        <DialogFooter>
          <Button
            variant="outline"
            onClick={() => onOpenChange(false)}
            className="border-white/15 bg-white/5 hover:bg-white/10 text-white"
          >
            Cancel
          </Button>
          <Button
            onClick={submit}
            disabled={!canSubmit}
            data-testid="pets-create-submit"
            className="bg-gradient-to-r from-fuchsia-500 to-cyan-500 text-white border-0 disabled:opacity-40"
          >
            {busy ? <Loader2 size={14} className="animate-spin mr-2" /> : <Upload size={14} className="mr-2" />}
            Create pet
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function Meter({ label, value, icon, color }) {
  const v = Math.round(value || 0);
  return (
    <div>
      <div className="flex items-center justify-between text-xs mb-1"><span className="flex items-center gap-1 text-white/60">{icon}{label}</span><span className="text-white/80 font-semibold">{v}%</span></div>
      <div className="h-2 bg-white/10 rounded-full overflow-hidden"><div className={`h-full ${color}`} style={{ width: `${v}%` }} /></div>
    </div>
  );
}
